/**
 * <custom-select> progressively enhances a native <select> with a fully
 * brand-styled trigger + option list, since browsers never let CSS reach
 * inside a native <select>'s dropdown popup.
 *
 * Markup expected inside the element:
 *   <custom-select class="select">
 *     <select>...</select>
 *     <span class="svg-wrapper">(caret icon, optional)</span>
 *   </custom-select>
 *
 * The native <select> is kept in the DOM (hidden) as the source of truth:
 * selecting a custom option updates its value and dispatches change/input
 * events, so it plugs into Dawn's existing facet filtering (facets.js)
 * without any changes there.
 */
class CustomSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select || this.querySelector('.custom-select__button')) return;

    this.select.classList.add('custom-select__native');
    this.select.setAttribute('tabindex', '-1');
    this.select.setAttribute('aria-hidden', 'true');

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'custom-select__button';
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');

    this.valueLabel = document.createElement('span');
    this.valueLabel.className = 'custom-select__value';

    const caretSource = this.querySelector('.svg-wrapper');
    const caret = document.createElement('span');
    caret.className = 'svg-wrapper custom-select__caret';
    caret.innerHTML = caretSource ? caretSource.innerHTML : '';
    if (caretSource) caretSource.remove();

    this.button.appendChild(this.valueLabel);
    this.button.appendChild(caret);

    this.listbox = document.createElement('ul');
    this.listbox.className = 'custom-select__listbox';
    this.listbox.setAttribute('role', 'listbox');
    this.listbox.hidden = true;

    Array.from(this.select.options).forEach((option) => {
      const item = document.createElement('li');
      item.className = 'custom-select__option';
      item.setAttribute('role', 'option');
      item.tabIndex = -1;
      item.dataset.value = option.value;
      item.textContent = option.textContent.trim();

      if (option.selected) {
        item.classList.add('selected');
        item.setAttribute('aria-selected', 'true');
        this.valueLabel.textContent = item.textContent;
      }

      item.addEventListener('click', () => this.selectOption(option.value, item));
      this.listbox.appendChild(item);
    });

    this.appendChild(this.button);
    this.appendChild(this.listbox);

    this.button.addEventListener('click', () => this.toggle());
    this.addEventListener('keydown', this.onKeyDown.bind(this));
    this.boundOnClickOutside = this.onClickOutside.bind(this);
  }

  toggle() {
    this.isOpen() ? this.close() : this.open();
  }

  isOpen() {
    return this.button.getAttribute('aria-expanded') === 'true';
  }

  open() {
    this.button.setAttribute('aria-expanded', 'true');
    this.listbox.hidden = false;
    document.addEventListener('click', this.boundOnClickOutside);
    const current = this.listbox.querySelector('.selected') || this.listbox.querySelector('.custom-select__option');
    if (current) current.focus();
  }

  close() {
    this.button.setAttribute('aria-expanded', 'false');
    this.listbox.hidden = true;
    document.removeEventListener('click', this.boundOnClickOutside);
  }

  onClickOutside(event) {
    if (!this.contains(event.target)) this.close();
  }

  selectOption(value, item) {
    this.select.value = value;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.select.dispatchEvent(new Event('input', { bubbles: true }));

    this.listbox.querySelectorAll('.custom-select__option').forEach((option) => {
      option.classList.remove('selected');
      option.removeAttribute('aria-selected');
    });
    item.classList.add('selected');
    item.setAttribute('aria-selected', 'true');
    this.valueLabel.textContent = item.textContent;

    this.close();
    this.button.focus();
  }

  onKeyDown(event) {
    const options = Array.from(this.listbox.querySelectorAll('.custom-select__option'));
    const focused = document.activeElement;
    const currentIndex = options.indexOf(focused);

    switch (event.key) {
      case 'Escape':
        this.close();
        this.button.focus();
        break;
      case 'Enter':
      case ' ':
        if (event.target === this.button) {
          event.preventDefault();
          this.toggle();
        } else if (options.includes(focused)) {
          event.preventDefault();
          this.selectOption(focused.dataset.value, focused);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen()) {
          this.open();
        } else {
          const next = options[Math.min(currentIndex + 1, options.length - 1)];
          if (next) next.focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.isOpen()) {
          const previous = options[Math.max(currentIndex - 1, 0)];
          if (previous) previous.focus();
        }
        break;
    }
  }
}

customElements.define('custom-select', CustomSelect);
